// src/components/exam-player/AiSectionRunner.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Send, Mic, Square, Loader2 } from "lucide-react";
import { httpRequest } from "../../utils/httpRequest";
import { toast } from "react-toastify";
import { BASE_URL } from "../../config";

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

function VoiceOrb({ active, level = 0, busy }) {
  const l = clamp01(level);
  const scale = 1 + l * 0.35;
  const glow = 0.12 + l * 0.5;

  return (
    <div className="flex flex-col items-center justify-center py-10">
      <div
        className="relative w-40 h-40 rounded-full transition-transform duration-100"
        style={{
          transform: `scale(${scale})`,
          boxShadow: `0 0 36px rgba(99,102,241,${glow})`,
          background: active
            ? "radial-gradient(circle at 30% 30%, rgba(99,102,241,0.95), rgba(79,70,229,1))"
            : "radial-gradient(circle at 30% 30%, rgba(148,163,184,0.8), rgba(100,116,139,1))",
        }}
      />
      {busy ? (
        <div className="mt-5 inline-flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="w-4 h-4 animate-spin" />
          در حال پردازش...
        </div>
      ) : null}
    </div>
  );
}

export default function AiSectionRunner({
  examId,
  section,
  sectionIndex,
  attemptRef,
  token,
  onDoneSection,
}) {
  const mode = section?.ai?.mode || "text";

  const [messages, setMessages] = useState([]); // برای ذخیره داخلی (نمایش نمی‌دیم در voice)
  const [text, setText] = useState("");

  const [starting, setStarting] = useState(false);
  const [sending, setSending] = useState(false);

  const [done, setDone] = useState(false);
  const [progress, setProgress] = useState(null);

  // voice states
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [recording, setRecording] = useState(false);

  const [audioUnlocked, setAudioUnlocked] = useState(false); // برای autoplay
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [level, setLevel] = useState(0);

  const aiAudioRef = useRef(null);

  // ✅ identity local: بعد از start از response پر میشه
  const [identity, setIdentity] = useState(() => ({
    attemptId: attemptRef?.attemptId || null,
    attemptKey: attemptRef?.attemptKey || null,
  }));

  useEffect(() => {
    const nextId = attemptRef?.attemptId || null;
    const nextKey = attemptRef?.attemptKey || null;

    // ✅ فقط وقتی چیزی داری از parent، overwrite کن
    if (nextId || nextKey) {
      setIdentity({ attemptId: nextId, attemptKey: nextKey });
    }
  }, [attemptRef?.attemptId, attemptRef?.attemptKey]);

  const attemptIdentity = useMemo(() => {
    if (identity?.attemptId) return { attemptId: identity.attemptId };
    if (identity?.attemptKey) return { attemptKey: identity.attemptKey };
    return {};
  }, [identity]);

  // recorder
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  // animation for orb
  const levelIvRef = useRef(null);
  const startLevelPulse = () => {
    stopLevelPulse();
    levelIvRef.current = setInterval(() => {
      setLevel((prev) => {
        const next = Math.random() * 0.9;
        return next * 0.7 + prev * 0.3;
      });
    }, 120);
  };
  const stopLevelPulse = () => {
    if (levelIvRef.current) clearInterval(levelIvRef.current);
    levelIvRef.current = null;
    setLevel(0);
  };

  // helper: play AI audio safely
  const playAiAudio = async (url) => {
    const el = aiAudioRef.current;
    if (!el || !url) return;

    // اگر url کامل نیست، از BASE_URL استفاده کن
    const fullUrl = url.startsWith("http")
      ? url
      : `https://api.testplus.online${url}`;

    el.src = fullUrl;
    el.preload = "auto";

    setAiSpeaking(true);
    startLevelPulse();

    try {
      await el.play();
    } catch (e) {
      setAiSpeaking(false);
      stopLevelPulse();
      toast.info("برای پخش صدا یک بار روی «شروع» بزنید.");
    }
  };

  // on AI audio ended → unlock user turn
  useEffect(() => {
    if (mode !== "voice") return;
    const el = aiAudioRef.current;
    if (!el) return;

    const onEnd = () => {
      setAiSpeaking(false);
      stopLevelPulse();
    };

    el.addEventListener("ended", onEnd);
    el.addEventListener("pause", onEnd);
    return () => {
      el.removeEventListener("ended", onEnd);
      el.removeEventListener("pause", onEnd);
    };
  }, [mode]);

  // ✅ start/resume session (فقط یکبار با تغییر examId/sectionIndex/token)
  useEffect(() => {
    if (!examId || sectionIndex == null || !token) return;

    (async () => {
      setStarting(true);
      try {
        const res = await httpRequest(
          "POST",
          "/ai/section/start",
          {
            exam: examId,
            sectionIndex: Number(sectionIndex), // ✅ مهم
            ...attemptIdentity,
          },
          { "x-access-token": token }
        );

        const data = res?.data?.data || res?.data;

        // ✅ identity را از response ذخیره کن (برای درخواست‌های بعدی)
        if (data?.attemptId || data?.attemptKey) {
          setIdentity({
            attemptId: data?.attemptId || null,
            attemptKey: data?.attemptKey || null,
          });
        }

        const msgs = Array.isArray(data?.messages) ? data.messages : [];
        setMessages(msgs);
        setDone(!!data?.done);
        if (data?.progress) setProgress(data.progress);

        // ✅ اگر voice هست و بک‌اند replyAudioUrl داد، بعد از unlock پخش می‌کنیم (اینجا فقط نگهش دار)
        // (پخش در unlockAudio انجام میشه)
      } catch (e) {
        toast.error(
          e?.response?.data?.message || e?.message || "AI start failed"
        );
      } finally {
        setStarting(false);
      }
    })();
    // ❗️attemptIdentity رو نذار وابستگی، وگرنه loop میشه وقتی setIdentity می‌خوره
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId, sectionIndex, token]);

  // -------- text mode: send
  const sendText = async () => {
    console.log("[FRONT] attemptIdentity used =", attemptIdentity);

    const trimmed = (text || "").trim();
    if (!trimmed || sending || done) return;

    setSending(true);
    setMessages((m) => [
      ...m,
      { role: "user", text: trimmed, createdAt: new Date().toISOString() },
    ]);
    setText("");

    try {
      const res = await httpRequest(
        "POST",
        "/ai/section/message",
        {
          exam: examId,
          sectionIndex: Number(sectionIndex), // ✅ مهم
          text: trimmed,
          ...attemptIdentity, // ✅ حتماً attemptId/Key
        },
        { "x-access-token": token }
      );
      const data = res?.data?.data || res?.data;

      const assistantMsg = {
        role: "assistant",
        text: data?.replyText || "",
        createdAt: new Date().toISOString(),
        audioUrl: data?.replyAudioUrl || null,
      };

      setMessages((m) => [...m, assistantMsg]);
      if (data?.progress) setProgress(data.progress);
      if (data?.done) setDone(true);
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "AI message failed"
      );
    } finally {
      setSending(false);
    }
  };

  // -------- voice mode: unlock audio (one-time)
  const unlockAudio = async () => {
    try {
      const el = aiAudioRef.current;
      if (!el) return;

      // iOS/Safari unlock trick
      el.muted = true;
      el.src =
        "data:audio/mp3;base64,//uQZAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACcQCA" +
        "AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA";
      await el.play().catch(() => {});
      el.pause();
      el.muted = false;

      setAudioUnlocked(true);

      // ✅ بعد از unlock، اگر آخرین پیام AI audioUrl داشت یا start replyAudioUrl داشت، پخش کن
      const lastAssistant = [...messages]
        .reverse()
        .find((m) => m.role === "assistant");
      const audioUrl = lastAssistant?.audioUrl || null;
      if (audioUrl) await playAiAudio(audioUrl);
      else toast.info("اگر صدایی پخش نمی‌شود، بک‌اند باید replyAudioUrl بدهد.");
    } catch {
      setAudioUnlocked(true);
    }
  };

  // -------- voice record
  const startRecording = async () => {
    if (recording || voiceBusy || starting || done) return;
    if (aiSpeaking) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);

      chunksRef.current = [];

      mr.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
      };

      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await sendVoiceBlob(blob);
      };

      recRef.current = mr;
      mr.start();
      setRecording(true);
      startLevelPulse();
    } catch {
      toast.error("Mic permission failed");
    }
  };

  const stopRecording = () => {
    if (!recording) return;
    try {
      recRef.current?.stop?.();
    } finally {
      setRecording(false);
      stopLevelPulse();
    }
  };

  const sendVoiceBlob = async (blob) => {
    console.log("[FRONT] attemptIdentity used =", attemptIdentity);
    setVoiceBusy(true);
    try {
      const fd = new FormData();
      fd.append("exam", examId);
      fd.append("sectionIndex", String(Number(sectionIndex))); // ✅ مهم
      if (attemptIdentity.attemptId)
        fd.append("attemptId", attemptIdentity.attemptId);
      if (attemptIdentity.attemptKey)
        fd.append("attemptKey", attemptIdentity.attemptKey);
      fd.append("audio", blob, "voice.webm");

      const resp = await fetch(`${BASE_URL}/ai/section/voice`, {
        method: "POST",
        headers: { "x-access-token": token },
        body: fd,
      });

      const json = await resp.json();
      if (!resp.ok) throw new Error(json?.message || "voice failed");
      const data = json?.data || json;

      // ذخیره داخلی
      if (data?.transcript) {
        setMessages((m) => [
          ...m,
          {
            role: "user",
            text: data.transcript,
            createdAt: new Date().toISOString(),
          },
        ]);
      }

      const assistantMsg = {
        role: "assistant",
        text: data?.replyText || "",
        audioUrl: data?.replyAudioUrl || null,
        createdAt: new Date().toISOString(),
      };
      setMessages((m) => [...m, assistantMsg]);

      if (data?.progress) setProgress(data.progress);
      if (data?.done) setDone(true);

      // ✅ play AI audio
      if (assistantMsg.audioUrl) {
        if (!audioUnlocked) toast.info("برای پخش صدا یک بار روی «شروع» بزنید.");
        else await playAiAudio(assistantMsg.audioUrl);
      } else {
        toast.warn(
          "replyAudioUrl از بک‌اند نیامده؛ TTS را در بک‌اند فعال کنید."
        );
      }
    } catch (e) {
      toast.error(e?.message || "Voice failed");
    } finally {
      setVoiceBusy(false);
    }
  };

  const finish = async () => {
    try {
      const res = await httpRequest(
        "POST",
        "/ai/section/finish",
        {
          exam: examId,
          sectionIndex: Number(sectionIndex),
          ...attemptIdentity,
        },
        { "x-access-token": token }
      );
      const data = res?.data?.data || res?.data;

      const aiResult = {
        turns: data?.turns || messages,
        summary: data?.summary || "",
        score: data?.score || 0,
        breakdown: data?.breakdown || {},
        mode,
      };

      onDoneSection?.(aiResult);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Finish failed");
    }
  };

  // ---------------------- VOICE UI ----------------------
  if (mode === "voice") {
    const busy = starting || voiceBusy;
    const canRecord = audioUnlocked && !busy && !done && !aiSpeaking;
    const canStop = recording && !busy;

    return (
      <div className="rounded-2xl border bg-white p-6">
        <audio ref={aiAudioRef} className="hidden" />

        <div className="flex items-center justify-between">
          <div className="text-lg font-semibold">
            {section?.title?.fa || section?.title?.en || "Voice AI"}
          </div>

          <button
            onClick={finish}
            disabled={!done}
            className={`px-3 py-2 rounded-lg text-white ${
              done ? "bg-rose-600 hover:bg-rose-700" : "bg-gray-300"
            }`}
          >
            Finish
          </button>
        </div>

        <VoiceOrb active={aiSpeaking || recording} level={level} busy={busy} />

        <div className="flex items-center justify-center gap-3 pb-2">
          {!audioUnlocked ? (
            <button
              onClick={unlockAudio}
              disabled={starting}
              className="px-6 py-3 rounded-xl bg-indigo-600 text-white inline-flex items-center gap-2 disabled:opacity-60"
            >
              {starting ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
              شروع
            </button>
          ) : (
            <button
              onClick={canStop ? stopRecording : startRecording}
              disabled={!canRecord && !canStop}
              className={`px-6 py-3 rounded-xl text-white inline-flex items-center gap-2 ${
                canStop
                  ? "bg-rose-600 hover:bg-rose-700"
                  : "bg-emerald-600 hover:bg-emerald-700"
              } ${
                !canRecord && !canStop ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {canStop ? (
                <Square className="w-5 h-5" />
              ) : (
                <Mic className="w-5 h-5" />
              )}
              {canStop ? "پایان" : "پاسخ"}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ---------------------- TEXT UI ----------------------
  const isLoading = starting || sending;

  return (
    <div className="rounded-2xl border bg-white p-5">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm text-gray-500 mb-1">AI Section</div>
          <div className="text-lg font-semibold">
            {section?.title?.fa || section?.title?.en || "AI"}
          </div>
          {progress ? (
            <div className="text-xs text-gray-500 mt-1">
              Answered {progress.answered}/{progress.total} • Asked{" "}
              {progress.asked}
            </div>
          ) : null}
        </div>

        <button
          onClick={finish}
          disabled={!done}
          className={`px-3 py-2 rounded-lg text-white ${
            done ? "bg-rose-600 hover:bg-rose-700" : "bg-gray-300"
          }`}
        >
          Finish
        </button>
      </div>

      <div className="border rounded-xl p-3 h-[50vh] overflow-y-auto bg-gray-50 space-y-3">
        {messages.map((m, idx) => (
          <div
            key={idx}
            className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
              m.role === "user"
                ? "ml-auto bg-indigo-600 text-white"
                : "mr-auto bg-white border"
            }`}
          >
            <div className="whitespace-pre-wrap">{m.text}</div>
          </div>
        ))}

        {isLoading ? (
          <div className="mr-auto bg-white border rounded-xl px-3 py-2 text-sm inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            در حال دریافت پاسخ...
          </div>
        ) : null}
      </div>

      <div className="mt-3 flex gap-2">
        <input
          className="flex-1 border rounded-lg px-3 py-2"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="پیام خود را بنویسید…"
          disabled={done || starting}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (!sending) sendText();
            }
          }}
        />
        <button
          onClick={sendText}
          disabled={!text.trim() || sending || done || starting}
          className="px-3 py-2 rounded-lg bg-emerald-600 text-white inline-flex items-center gap-2 disabled:opacity-50"
        >
          {sending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          Send
        </button>
      </div>
    </div>
  );
}
